import { useState, useEffect, useMemo } from 'react';
import api from '../../../lib/api';
import { Button } from '../../../components/ui/button';
import { Badge } from '../../../components/ui/badge';
import { Label } from '../../../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../../../components/ui/dialog';
import { Loader2, Building2 } from 'lucide-react';
import { toast } from 'sonner';
import { ResolveRow } from './ResolveRow';

const initRow = (r, initialMode) => {
  const linkTarget = r.match?.id || (initialMode === 'link' ? r.candidates[0]?.id : '') || '';
  return {
    row_key: r.row_key, bill_id: r.bill_id,
    mode: linkTarget ? 'link' : 'create', acct_id: linkTarget,
    account_number: r.account_number || '', commodity: (r.commodity || 'electricity').toLowerCase(),
    service_address: r.service_address || '', service_city: r.service_city || '',
    service_state: r.service_state || '', service_zip: r.service_zip || '',
    meter_numbers: r.meters.map(m => m.number),
    vendor_id: r.vendor_match?.id || '', new_vendor_name: r.vendor_match ? '' : (r.vendor_name || ''),
    linked_location_id: r.property_match?.id || '',
    new_property: r.property_match ? null : {
      name: r.service_address || '', address: r.service_address || '',
      city: r.service_city || '', state: r.service_state || '', zip: r.service_zip || '',
    },
  };
};

const Stat = ({ label, s, testid }) => (
  <div className="rounded-md border px-3 py-2 min-w-[110px]" data-testid={testid}>
    <div className="text-[10px] uppercase tracking-wide text-muted-foreground">{label}</div>
    <div className="text-lg font-semibold leading-tight">{s?.total ?? 0}</div>
    <div className="text-[11px]"><span className="text-sky-600">{s?.existing ?? 0} existing</span> · <span className="text-emerald-600">{s?.new ?? 0} new</span></div>
  </div>
);

const plan = (rows, preview) => {
  const meta = Object.fromEntries((preview?.rows || []).map(r => [r.row_key, r]));
  const p = { link: 0, create: 0, skip: 0, meters: new Set(), props: new Set(), vendors: new Set() };
  rows.forEach(r => {
    p[r.mode] += 1;
    if (r.mode === 'skip') return;
    (meta[r.row_key]?.meters || []).forEach(m => { if (!m.exists && r.meter_numbers.includes(m.number)) p.meters.add(m.number.toLowerCase()); });
    if (r.mode === 'create' && !meta[r.row_key]?.match) {
      if (!r.linked_location_id && r.new_property) p.props.add((r.new_property.address || r.new_property.name || '').toLowerCase().trim());
      if (!r.vendor_id && r.new_vendor_name) p.vendors.add(r.new_vendor_name.toLowerCase().trim());
    }
  });
  return p;
};

export const ResolveDialog = ({ open, onOpenChange, accountId, bill, initialMode, onDone }) => {
  const [loading, setLoading] = useState(false);
  const [preview, setPreview] = useState(null);
  const [rows, setRows] = useState([]);
  const [type, setType] = useState('utility');
  const [vendors, setVendors] = useState([]);
  const [properties, setProperties] = useState([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open || !bill) return;
    setLoading(true); setPreview(null);
    const p = { params: { account_id: accountId } };
    Promise.all([
      api.get(`/orphan-bill-link/bills/${bill.bill_id}/resolve-preview`, p),
      api.get('/orphan-bill-link/properties', p),
      api.get('/orphan-bill-link/vendors', p),
    ]).then(([pv, props, vend]) => {
      setPreview(pv.data);
      setType(pv.data.create_account_type || 'utility');
      setRows((pv.data.rows || []).map(r => initRow(r, initialMode)));
      setProperties(props.data.properties || []);
      setVendors(vend.data.vendors || []);
    }).catch(e => toast.error(e.response?.data?.detail || 'Failed to load bill accounts'))
      .finally(() => setLoading(false));
  }, [open, bill, accountId, initialMode]);

  const p = useMemo(() => plan(rows, preview), [rows, preview]);
  const setRow = (i, patch) => setRows(prev => prev.map((r, idx) => idx === i ? { ...r, ...patch } : r));

  const submit = async () => {
    const active = rows.filter(r => r.mode !== 'skip');
    if (!active.length) { toast.error('Nothing to do — every account is skipped'); return; }
    if (active.some(r => r.mode === 'link' && !r.acct_id)) { toast.error('Choose an account for each linked row'); return; }
    if (active.some(r => r.mode === 'create' && !r.account_number.trim())) { toast.error('Account number is required'); return; }
    if (active.some(r => r.mode === 'create' && !r.linked_location_id && !(r.new_property?.name || r.new_property?.address))) { toast.error('Select or create a property for each new account'); return; }
    setSaving(true);
    try {
      const { data } = await api.post(`/orphan-bill-link/bills/${bill.bill_id}/resolve`, { type, rows: active }, { params: { account_id: accountId } });
      const s = data.stats || {};
      toast.success(`Linked ${s.bills_linked || 0} bill(s) · ${s.accounts_created || 0} account(s) created · ${s.accounts_linked || 0} linked · ${s.properties_created || 0} propert(ies) · ${s.vendors_created || 0} vendor(s) · ${s.meters_created || 0} meter(s) created`);
      onOpenChange(false); onDone();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to resolve bill'); }
    finally { setSaving(false); }
  };

  const s = preview?.summary;
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[88vh] overflow-y-auto" data-testid="obl-resolve-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Building2 className="h-4 w-4" /> Link / create accounts for this bill</DialogTitle>
          <DialogDescription>
            {bill?.vendor_name} · <span className="font-mono">{bill?.account_number || '—'}</span>
            {preview?.bill?.is_consolidated && <Badge variant="outline" className="ml-2 text-[10px]">Consolidated</Badge>}
            {preview?.bill?.is_collective && <Badge variant="outline" className="ml-2 text-[10px]">Collective · {s?.bills} bills</Badge>}
          </DialogDescription>
        </DialogHeader>
        {loading || !preview ? (
          <div className="py-10 text-center text-muted-foreground"><Loader2 className="h-5 w-5 animate-spin inline mr-2" /> Reading the bill…</div>
        ) : (
          <div className="space-y-4">
            <div className="flex flex-wrap gap-2" data-testid="obl-resolve-summary">
              <Stat label="Accounts" s={s.accounts} testid="obl-sum-accounts" />
              <Stat label="Meters" s={s.meters} testid="obl-sum-meters" />
              <Stat label="Properties" s={s.properties} testid="obl-sum-properties" />
              <Stat label="Vendors" s={s.vendors} testid="obl-sum-vendors" />
              <div className="rounded-md border px-3 py-2 min-w-[110px]" data-testid="obl-sum-identifiers">
                <div className="text-[10px] uppercase tracking-wide text-muted-foreground">Identifiers</div>
                <div className="text-lg font-semibold leading-tight">{s.identifiers}</div>
                <div className="text-[11px] text-muted-foreground">service lines</div>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <Label className="text-xs">Create new accounts as</Label>
              <Select value={type} onValueChange={setType}>
                <SelectTrigger className="h-8 w-56 text-xs" data-testid="obl-create-type"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="utility">Common Area — Utility Account</SelectItem>
                  <SelectItem value="vendor">Vendor Account</SelectItem>
                </SelectContent>
              </Select>
              <span className="text-[11px] text-muted-foreground">Remembered for next time</span>
            </div>
            <div className="space-y-3">
              {preview.rows.map((r, i) => (
                <ResolveRow key={r.row_key} row={r} st={rows[i]} set={patch => setRow(i, patch)} i={i} vendors={vendors} properties={properties} />
              ))}
            </div>
          </div>
        )}
        <DialogFooter className="flex-col sm:flex-row sm:items-center gap-2">
          {preview && (
            <p className="text-[11px] text-muted-foreground mr-auto" data-testid="obl-resolve-plan">
              Will link {p.link} · create {p.create} account(s) · {p.props.size} new propert(ies) · {p.vendors.size} new vendor(s) · {p.meters.size} new meter(s){p.skip ? ` · skip ${p.skip}` : ''}. Existing records are reused, never duplicated.
            </p>
          )}
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={submit} disabled={saving || loading || !preview} data-testid="obl-resolve-confirm">
            {saving ? 'Saving…' : 'Confirm & Link'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
